import {
  Autocomplete as MUIAutocomplete,
  Box,
  Checkbox,
  CircularProgress,
} from "@mui/material";

import { CheckBox, CheckBoxOutlineBlank, Clear } from "@mui/icons-material";

import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const SELECT_ALL_OPTION = { id: "select-all", label: "Select All" };
const DEFAULT_PAGE_SIZE = 15;
const SCROLL_END_THRESHOLD_IN_PX = 8;

const getUnselectedOptions = (target, currentOptions) =>
  Array.from(
    target
      .closest("ul")
      .querySelectorAll("[aria-selected='false']:not([data-option-index='0'])")
  ).map((item) =>
    currentOptions.at(Number(item.getAttribute("data-option-index")))
  );

const filterOptions = (selectedOptions, currentOptions) => {
  const idsToFilter = currentOptions.map(({ id }) => id);
  return selectedOptions.filter((option) => !idsToFilter.includes(option.id));
};

const handleSelectAll = (callback, selectAll, target, currentOptions) => {
  if (selectAll) {
    callback((previous) => [
      ...previous,
      ...getUnselectedOptions(target, currentOptions),
    ]);
  } else {
    callback((previous) => filterOptions(previous, currentOptions));
  }
};

const hasUnselectedOptions = (target, currentOptions) => {
  const index = Number(target.getAttribute("data-option-index"));

  const itemsAvailable = target
    .closest("ul")
    .querySelectorAll(
      `[aria-selected='false']:not([data-option-index='0']):not([data-option-index='${index}'])`
    );

  return itemsAvailable.length === 0;
};

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;
const deleteIcon = (
  <Clear fontSize="small" variant="filled" sx={{ color: "#fff !important" }} />
);

const DEFAULT_CHIP_PROPS = {
  sx: { background: "#64B5C5" },
  deleteIcon: deleteIcon,
  variant: "filled",
  color: "primary",
};

const DEFAULT_CHECKBOX_PROPS = { icon, checkedIcon, style: { marginRight: 8 } };

const LoadingMoreContext = createContext(false);

// The Select All option occupies the first slot of the option list.
const countLoadedOptions = (options) => Math.max(options.length - 1, 0);

const Listbox = forwardRef(({ children, ...props }, ref) => {
  const isLoadingMore = useContext(LoadingMoreContext);

  return (
    <ul ref={ref} {...props}>
      {children}
      {isLoadingMore && (
        <Box
          component="li"
          sx={{ display: "flex", justifyContent: "center", py: 1 }}
        >
          <CircularProgress size={20} aria-label="Loading more options" />
        </Box>
      )}
    </ul>
  );
});

const isAllOptionsSelected = (values, newOptions) => {
  for (let value of values) {
    if (
      value.id !== "select-all" &&
      !newOptions.find(({ id }) => id === value.id)
    ) {
      return false;
    }
  }

  return values.length > 0;
};

const Autocomplete = ({
  defaultValue,
  getServerOptions,
  onValueChange,
  pageSize = DEFAULT_PAGE_SIZE,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isLoadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [value, setValue] = useState(defaultValue ?? []);
  const optionsRef = useRef([]);
  const valueRef = useRef(defaultValue ?? []);
  const requestIdRef = useRef(0);
  const isLoadingMoreRef = useRef(false);
  const hasMoreOptionsRef = useRef(false);
  const listboxRef = useRef(null);

  const setNewOptions = (options) => {
    setSelectAll(isAllOptionsSelected(options, valueRef.current));
    setOptions(options);
    optionsRef.current = options;
  };

  const commitPage = useCallback(
    (page, options, totalCount) => {
      // A page shorter than requested means the server has nothing left to give,
      // whatever the reported total says.
      hasMoreOptionsRef.current =
        page.length >= pageSize && countLoadedOptions(options) < totalCount;
      setNewOptions(options);
    },
    [pageSize]
  );

  const handleValueChange = (argOrFn) => {
    setValue((value) => {
      const newValue = typeof argOrFn === "function" ? argOrFn(value) : argOrFn;

      valueRef.current = newValue;
      onValueChange && onValueChange(newValue);

      return newValue;
    });
  };

  useEffect(() => {
    // Any page still in flight belongs to a previous search term or session. The
    // options of that term stay on screen until the new first page lands, so
    // paging stays closed until then to keep terms from being mixed together.
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    isLoadingMoreRef.current = false;
    hasMoreOptionsRef.current = false;
    setLoadingMore(false);

    if (open && getServerOptions) {
      setLoading(true);
      getServerOptions(searchTerm, { limit: pageSize, offset: 0 })
        .then(({ options: result, totalCount }) => {
          if (requestId !== requestIdRef.current) return;

          commitPage(
            result,
            result.length > 0 ? [SELECT_ALL_OPTION, ...result] : [],
            totalCount
          );
        })
        .finally(() => {
          if (requestId === requestIdRef.current) setLoading(false);
        });
    } else {
      setNewOptions([]);
      setLoading(false);
    }
  }, [open, searchTerm, getServerOptions, pageSize, commitPage]);

  const loadMoreOptions = useCallback(() => {
    if (!hasMoreOptionsRef.current || isLoadingMoreRef.current) return;

    const offset = countLoadedOptions(optionsRef.current);
    const requestId = requestIdRef.current;
    isLoadingMoreRef.current = true;
    setLoadingMore(true);

    getServerOptions(searchTerm, { limit: pageSize, offset })
      .then(({ options: result, totalCount }) => {
        if (requestId !== requestIdRef.current) return;

        commitPage(result, [...optionsRef.current, ...result], totalCount);
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;

        isLoadingMoreRef.current = false;
        setLoadingMore(false);
      });
  }, [commitPage, getServerOptions, pageSize, searchTerm]);

  // The listbox is capped at 40vh, so a page that leaves the list too short to
  // scroll would strand every remaining page behind a scroll that never happens.
  useEffect(() => {
    const listbox = listboxRef.current;

    if (!listbox || listbox.clientHeight === 0) return;

    const hiddenHeight = listbox.scrollHeight - listbox.clientHeight;

    if (hiddenHeight <= SCROLL_END_THRESHOLD_IN_PX) {
      loadMoreOptions();
    }
  }, [options, loadMoreOptions]);

  const handleListboxScroll = (event) => {
    const listbox = event.currentTarget;
    const distanceToEnd =
      listbox.scrollHeight - listbox.scrollTop - listbox.clientHeight;

    if (distanceToEnd <= SCROLL_END_THRESHOLD_IN_PX) {
      loadMoreOptions();
    }
  };

  const handleSelect = (event, newValue, reason, { option }) => {
    const target = event.currentTarget;
    if (option.id === "select-all") {
      handleSelectAll(
        handleValueChange,
        !selectAll,
        target,
        optionsRef.current
      );
      setSelectAll((prev) => !prev);
    } else {
      const isAllSelected =
        reason === "selectOption" &&
        hasUnselectedOptions(target, optionsRef.current);
      handleValueChange(newValue);
      setSelectAll(isAllSelected);
    }
  };
  const handleClear = (...args) => {
    handleValueChange([]);
    setSelectAll(false);
  };

  return (
    <LoadingMoreContext.Provider value={isLoadingMore}>
      <MUIAutocomplete
        options={options}
        open={open}
        loading={isLoading}
        value={value}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disableCloseOnSelect
        filterOptions={(v) => v}
        multiple
        onChange={(event, newValue, reason, details) => {
          const handler = reason === "clear" ? handleClear : handleSelect;
          handler(event, newValue, reason, details);
        }}
        freeSolo
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, option, { selected }) => {
          const { key, ...otherProps } = props;
          const isSelectAll = option.id === "select-all";
          const listProps = isSelectAll ? { "aria-selected": selectAll } : {};

          return (
            <li key={key} {...otherProps} {...listProps}>
              <Checkbox
                {...DEFAULT_CHECKBOX_PROPS}
                checked={isSelectAll ? selectAll : selected}
              />
              {option.label}
            </li>
          );
        }}
        inputValue={searchTerm}
        onInputChange={(_event, value, reason) => {
          setSearchTerm(value);
        }}
        ChipProps={DEFAULT_CHIP_PROPS}
        ListboxComponent={Listbox}
        ListboxProps={{ onScroll: handleListboxScroll, ref: listboxRef }}
        {...props}
      />
    </LoadingMoreContext.Provider>
  );
};

export default Autocomplete;
